import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { chiusureApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Upload, 
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  Trash2,
  History,
  Undo2,
  ShieldCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const ImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [result, setResult] = useState(null);

  // Backup / restore
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const loadBackups = useCallback(async () => {
    setLoadingBackups(true);
    try {
      const rows = await chiusureApi.listBackups();
      setBackups(rows);
    } catch (e) {
      // Se non c'è locale attivo il backend risponde 400: non è un errore utente
      if (e.response?.status !== 400) {
        console.error('[Import] listBackups', e);
      }
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await chiusureApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_chiusure.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template scaricato!');
    } catch (error) {
      // Il server potrebbe rispondere con un blob contenente JSON di errore
      // (nel caso di errori 4xx/5xx quando responseType='blob'): lo estraiamo
      // per mostrare il vero motivo all'utente.
      let detail = 'Errore nel download del template';
      const status = error?.response?.status;
      const data = error?.response?.data;
      try {
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            if (parsed?.detail) detail = parsed.detail;
          } catch (_e) {
            if (text) detail = text.slice(0, 200);
          }
        } else if (data?.detail) {
          detail = data.detail;
        }
      } catch (_e) { /* fallback silenzioso */ }
      const msg = status ? `[${status}] ${detail}` : detail;
      console.error('[Import] Template download failed:', status, detail, error);
      toast.error(msg);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      toast.error('Seleziona un file Excel (.xlsx o .xls)');
      return;
    }
    
    setFile(selectedFile);
    setPreview(null);
    setResult(null);
    setLoading(true);
    
    try {
      const previewData = await chiusureApi.importPreview(selectedFile);
      setPreview(previewData);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella lettura del file');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    try {
      const importResult = await chiusureApi.importConfirm(file);
      setResult(importResult);
      toast.success(`Import completato! ${importResult.created} creati, ${importResult.updated} aggiornati`);
      loadBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await chiusureApi.restoreBackup(restoreTarget.backup_id);
      toast.success(
        `Ripristino completato. Ripristinate ${res.restored} chiusure (eliminate ${res.deleted_current} attuali).`
      );
      setRestoreTarget(null);
      setResult(null);
      loadBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel ripristino');
    } finally {
      setRestoring(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/analytics')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold font-heading tracking-tight">Import Dati</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Formato file richiesto</h3>
            <p className="text-sm text-blue-700 mb-2">
              Carica un file Excel (.xlsx) con queste colonne:
            </p>
            <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
              <li>Data Servizio (obbligatorio)</li>
              <li>Coperti Pranzo, Contanti Pranzo, POS Pranzo, ...</li>
              <li>Coperti Cena, Contanti Cena, POS Cena, ...</li>
              <li>Totale Scontrini, Totale Fatture, Totale Cassa Cloud</li>
            </ul>
            <p className="text-xs text-blue-600 mt-2 italic">
              I campi calcolati (incassi totali, proforma, delta) verranno ricalcolati automaticamente.
            </p>
          </CardContent>
        </Card>

        {/* Upload Area */}
        {!file && (
          <Card className="border-dashed border-2">
            <CardContent className="p-8">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="flex flex-col items-center justify-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="font-semibold mb-1">Clicca per caricare un file</p>
                <p className="text-sm text-muted-foreground">File Excel (.xlsx, .xls)</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <Card>
            <CardContent className="p-8 flex flex-col items-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p>Analisi del file in corso...</p>
            </CardContent>
          </Card>
        )}

        {/* File Selected + Preview */}
        {file && preview && !result && (
          <>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-semibold">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {preview.total_rows} righe trovate, {preview.valid_rows} valide
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={handleReset}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Errors */}
            {preview.errors.length > 0 && (
              <Card className="border-amber-300 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    Avvisi ({preview.errors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {preview.errors.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Preview Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Anteprima (prime 10 righe)</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-2 text-left">Data</th>
                      <th className="p-2 text-right">Cop. P</th>
                      <th className="p-2 text-right">Cop. C</th>
                      <th className="p-2 text-right">Contanti</th>
                      <th className="p-2 text-right">POS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2 font-mono-numbers">{row.data_servizio}</td>
                        <td className="p-2 text-right font-mono-numbers">{row.coperti_pranzo || 0}</td>
                        <td className="p-2 text-right font-mono-numbers">{row.coperti_cena || 0}</td>
                        <td className="p-2 text-right font-mono-numbers">
                          {formatCurrency((row.contanti_pranzo || 0) + (row.contanti_cena || 0))}
                        </td>
                        <td className="p-2 text-right font-mono-numbers">
                          {formatCurrency((row.pos_pranzo || 0) + (row.pos_cena || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Import Button */}
            <Button 
              onClick={handleImport}
              disabled={importing || preview.valid_rows === 0}
              className="w-full h-14 text-lg font-bold"
              data-testid="confirm-import-btn"
            >
              {importing ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Importa {preview.valid_rows} chiusure
                </>
              )}
            </Button>
          </>
        )}

        {/* Result */}
        {result && (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mb-4" />
                <h3 className="text-xl font-bold text-green-800 mb-2">Import completato!</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-3xl font-bold font-mono-numbers text-green-700">{result.created}</p>
                    <p className="text-sm text-green-600">Nuove chiusure</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold font-mono-numbers text-blue-700">{result.updated}</p>
                    <p className="text-sm text-blue-600">Aggiornate</p>
                  </div>
                </div>
                {result.errors.length > 0 && (
                  <div className="text-left w-full mt-4 p-3 bg-amber-100 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800 mb-1">Errori ({result.errors.length}):</p>
                    <ul className="text-xs text-amber-700 space-y-1">
                      {result.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.backup_id && (
                  <div className="text-left w-full mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-900">
                        <p className="font-semibold mb-1">Backup automatico creato</p>
                        <p>
                          Sono state salvate <strong>{result.n_snapshotted}</strong> chiusure nello stato pre-import.
                          Se qualcosa non torna, puoi ripristinare tutto dalla sezione <em>Backup disponibili</em> qui sotto.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={handleReset}>
                    Nuovo import
                  </Button>
                  <Button onClick={() => navigate('/chiusure')}>
                    Vai alle chiusure
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Backup disponibili */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-primary" /> Backup pre-import disponibili
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Prima di ogni import viene salvato uno snapshot completo delle chiusure del locale attivo,
              così puoi tornare indietro in qualsiasi momento se l'import ha esiti indesiderati.
            </p>
            {loadingBackups ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : backups.length === 0 ? (
              <p className="text-sm text-muted-foreground italic pt-2">
                Nessun backup ancora presente per questo locale.
              </p>
            ) : (
              <div className="space-y-2">
                {backups.map((b) => (
                  <div
                    key={b.backup_id}
                    className="flex items-center justify-between gap-3 p-3 rounded-md border bg-background text-sm"
                    data-testid={`backup-row-${b.backup_id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {b.filename || 'Import senza nome file'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(b.created_at), 'd MMM yyyy, HH:mm', { locale: it })}
                        {' · '}
                        <span className="font-mono">{b.n_chiusure}</span> chiusure salvate
                        {b.restored && <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">Già ripristinato</Badge>}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setRestoreTarget(b)}
                      data-testid={`restore-btn-${b.backup_id}`}
                    >
                      <Undo2 className="w-3 h-3 mr-1" /> Ripristina
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Download Template */}
        <Card className="border-dashed">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Hai bisogno di un modello per importare i dati?
            </p>
            <Button 
              variant="outline" 
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              className="gap-2"
            >
              {downloadingTemplate ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Scarica Template Excel
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Dialog conferma restore backup */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-primary" /> Ripristina backup
            </DialogTitle>
            <DialogDescription>
              Le chiusure attuali del locale verranno <strong>completamente sostituite</strong>{' '}
              con quelle contenute in questo backup.
            </DialogDescription>
          </DialogHeader>

          {restoreTarget && (
            <div className="p-3 rounded-md bg-muted text-sm space-y-1">
              <p><strong>File:</strong> {restoreTarget.filename || '—'}</p>
              <p><strong>Data backup:</strong> {format(new Date(restoreTarget.created_at), 'd MMM yyyy, HH:mm', { locale: it })}</p>
              <p><strong>Chiusure incluse:</strong> {restoreTarget.n_chiusure}</p>
            </div>
          )}

          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              L'operazione <strong>elimina lo stato corrente</strong> delle chiusure di questo locale e
              lo sostituisce con lo snapshot. Verrà creato automaticamente un nuovo backup prima del
              prossimo import, quindi non stai perdendo nulla per sempre.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>Annulla</Button>
            <Button
              onClick={handleRestore}
              disabled={restoring}
              data-testid="confirm-restore-btn"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Ripristina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportPage;
